import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table";
import PaginatedTable from "../Common/PaginatedTable";
import { HiPencilAlt, HiXCircle } from "react-icons/hi";
import { displayDateOnly } from "../../Utils";
import { Job } from "../../Models/Job";
import { useMemo } from "react";

export default function JobListTable({
  data,
  deleteJob,
  editJob,
}: {
  data: Job[];
  deleteJob(id: number): void;
  editJob: (inv: Job) => void;
}) {
  const columnHelper = createColumnHelper<Job>();

  const columns = useMemo<ColumnDef<Job, any>[]>(
    () => [
      // ✅ Actions as a display column with a unique id
      columnHelper.display({
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex justify-center gap-1">
            <button
              type="button"
              className="buttonMain p-2"
              onClick={() => editJob(row.original)}
            >
              <HiPencilAlt />
            </button>
            <button
              type="button"
              className="buttonCancel p-2"
              onClick={() => deleteJob(row.original.id)}
            >
              <HiXCircle />
            </button>
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        size: 35,
      }),

      // ✅ Real Job Id column with a different id
      columnHelper.accessor("id", {
        id: "jobId",
        header: "Id",
        cell: (info) => <span>{info.getValue()}</span>,
        sortingFn: "alphanumeric",
      }),

      columnHelper.accessor((row) => row.client?.name, {
        id: "client",
        header: "Client",
        cell: ({ row }) => <span>{row.original.client?.name}</span>,
        sortingFn: "alphanumeric",
      }),

      columnHelper.accessor((row) => row.clientLocation?.clientSiteId, {
        id: "clientSiteId",
        header: "Name",
        cell: ({ row }) => (
          <i
            className="cursor-pointer text-blue-600"
            onClick={() => editJob(row.original)}
          >
            {row.original.clientLocation?.clientSiteId}
          </i>
        ),
        sortingFn: "alphanumeric",
      }),

      columnHelper.accessor((row) => row.clientLocation?.id, {
        id: "clientLocationId",
        header: "Location",
        cell: ({ row }) => {
          const loc = row.original.clientLocation;
          return (
            <i
              className="cursor-pointer text-blue-600"
              onClick={() => editJob(row.original)}
            >
              {loc?.address} {loc?.city} {loc?.state ? loc.state.name : ""}
            </i>
          );
        },
        sortingFn: "alphanumeric",
        enableColumnFilter: false,
      }),

      columnHelper.accessor((row) => row.jobNumber, {
        id: "jobNumber",
        header: "PO Number",
        sortingFn: "alphanumeric",
        filterFn: "includesString",
        enableColumnFilter: false,
      }),

      columnHelper.accessor((row) => row.startDate, {
        id: "startDate",
        header: "Start",
        cell: (info) => (
          <i>
            {displayDateOnly(
              info.getValue() ? new Date(info.getValue()!) : null
            )}
          </i>
        ),
        sortingFn: "alphanumeric",
        enableColumnFilter: false,
      }),

      columnHelper.accessor((row) => row.endDate, {
        id: "endDate",
        header: "End",
        cell: (info) => (
          <i>
            {displayDateOnly(
              info.getValue() ? new Date(info.getValue()!) : null
            )}
          </i>
        ),
        sortingFn: "alphanumeric",
        enableColumnFilter: false,
      }),

      // Prefer a numeric accessor instead of returning null
      columnHelper.accessor((row) => row.jobItems?.length ?? 0, {
        id: "jobItems",
        header: "Delivery Items",
        cell: (info) => <i>{info.getValue()}</i>,
        sortingFn: "basic",
        enableColumnFilter: false,
      }),
    ],
    [deleteJob, editJob, columnHelper]
  );

  const table = useReactTable<Job>({
    data,
    columns,
    getRowId: (row) => String(row.id), // stable ids help state
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
      sorting: [{ id: "jobId", desc: false }], // ✅ sort by the unique job id column
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <div className="w-full mx-auto overflow-x-auto">
      <PaginatedTable
        table={table}
        dataCount={data.length}
        columnResizeMode="onChange"
        showFilters={true}
      />
    </div>
  );
}
